import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Settings, Building2, Palette, Save } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';
import SmtpSettingsPanel from '../components/SmtpSettingsPanel';

export default function OrgSettings() {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({ name: '', brand_color: '#1d4ed8' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = () => {
    API.get('/organizations/my')
      .then(r => {
        setOrg(r.data);
        if (r.data) {
          setForm({ name: r.data.name, brand_color: r.data.brand_color || '#1d4ed8' });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Organization name required'); return; }
    setCreating(true);
    try {
      const r = await API.post('/organizations', form);
      setOrg(r.data);
      toast.success('Organization created!');
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!org) return;
    setSaving(true);
    try {
      await API.put(`/organizations/${org.id}`, form);
      toast.success('Settings saved');
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="p-8 flex justify-center"><div className="w-6 h-6 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>
  );

  return (
    <div className="p-6 md:p-8 fade-in max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your account and organization</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Settings size={15} className="text-slate-500" /> Account
        </h2>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-blue-100 rounded-sm flex items-center justify-center">
            <span className="text-blue-700 font-bold text-xl" style={{ fontFamily: 'Outfit' }}>{(user?.name || 'U')[0].toUpperCase()}</span>
          </div>
          <div>
            <p className="font-semibold text-slate-900">{user?.name}</p>
            <p className="text-sm text-slate-500">{user?.email}</p>
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-sm capitalize font-medium">{user?.role}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-5 mb-5">
        <h2 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Building2 size={15} className="text-slate-500" /> Organization
        </h2>

        {!org ? (
          <div>
            <p className="text-sm text-slate-500 mb-4">Create an organization for team branding. SMTP email settings are below.</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Organization Name *</label>
                <input
                  type="text" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Corp"
                  data-testid="org-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                  <Palette size={13} /> Brand Color
                </label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.brand_color} onChange={e => setForm(p => ({ ...p, brand_color: e.target.value }))} className="w-10 h-10 rounded border border-slate-300 cursor-pointer" data-testid="brand-color-input" />
                  <span className="text-sm text-slate-600 font-mono">{form.brand_color}</span>
                </div>
              </div>
              <button type="submit" disabled={creating} className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60" data-testid="create-org-btn">
                {creating ? 'Creating...' : 'Create Organization'}
              </button>
            </form>
          </div>
        ) : (
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Organization Name</label>
              <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="org-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <Palette size={13} /> Brand Color
              </label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.brand_color} onChange={e => setForm(p => ({ ...p, brand_color: e.target.value }))} className="w-10 h-10 rounded border border-slate-300 cursor-pointer" data-testid="brand-color-input" />
                <span className="text-sm text-slate-600 font-mono">{form.brand_color}</span>
              </div>
            </div>
            <button type="submit" disabled={saving} className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60" data-testid="save-settings-btn">
              <Save size={14} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        )}
      </div>

      <SmtpSettingsPanel />
    </div>
  );
}
